import { Box, Fallback, HStack } from '@cbhq/cds-web/layout';
import { VStack } from '@cbhq/cds-web/layout';
import { Select, SelectOption } from '@cbhq/cds-web/controls';
import { Icon } from '@cbhq/cds-web/icons';
import { TextHeadline, TextTitle3, TextBody } from '@cbhq/cds-web/typography';
import { memo, useMemo } from 'react';
import { useChainId } from 'wagmi';
import { RemoteImage, RemoteImageGroup } from '@cbhq/cds-web/media';
import { assetByAddress, AssetConfig } from '@/onchain/assets';
import { TagLabel } from '@/components/TagLabels/TagLabel';
import { useAddLiquidityState } from '@/components/AddLiquidity/AddLiquidityProvider';
import { USDCRewardsChip } from '@/components/common/USDCRewardsChip';
import { isToken0PreferredQuoteToken } from '@/utils/tokenPreference';
import { EditAddLiquiditySettings } from '@/components/AddLiquidity/EditAddLiquiditySettings';
import { usePoolsInfo } from '@/components/Pools/hooks/usePoolsInfo';
import { format, DisplayType } from '@/utils/numberFormatter/formatNumbers';
import { PoolsListTokensMedia } from '@/components/Pools/PoolsListTokensMedia';
import {
  NESTED_CARD_PADDING,
  NESTED_CARD_BORDER_RADIUS,
  NESTED_CARD_GAP,
} from '@/constants/layout';

// Number formatters for fee and APR display
const numberFormatters = {
  percentFee: new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  apr: new Intl.NumberFormat('en', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }),
};

type TokenPairSelectorProps = {
  symbol: string;
  imageUrl: string;
  currencies: string[];
  handleSelect: (value: string) => void;
  disabled?: boolean;
  tokensMap: Record<string, AssetConfig | undefined>;
  testID: string;
};

export function AddLiquidityTokenPairSelectorLoading({
  positionId,
}: {
  positionId: string | null;
}) {
  if (positionId)
    return (
      <HStack alignItems="center" gap={1} overflow="scroll" paddingY={1}>
        <HStack>
          <RemoteImageGroup size={24} shape="circle" testID={`position-${positionId}-tokens`}>
            <Fallback width={24} height={24} shape="circle" />
            <Fallback width={24} height={24} shape="circle" />
          </RemoteImageGroup>
        </HStack>
        <HStack alignItems="center" gap={1}>
          <Fallback width={120} height={22} shape="squircle" />
          <Fallback width={55} height={22} shape="squircle" />
          <Fallback width={70} height={22} shape="squircle" />
        </HStack>
      </HStack>
    );
  return (
    <VStack gap={1.5}>
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <TextHeadline as="p" testID="token-pair-selector-title" noWrap>
          Choose a pair
        </TextHeadline>
        <EditAddLiquiditySettings />
      </HStack>
      <HStack gap={1.5} alignItems="stretch" testID="token-pair-selector-container" width="100%">
        <Box flexGrow={1} flexShrink={1} width="50%">
          <Fallback width="100%" height={46} shape="squircle" />
        </Box>
        <Box flexGrow={1} flexShrink={1} width="50%">
          <Fallback width="100%" height={46} shape="squircle" />
        </Box>
      </HStack>

      <HStack gap={0.5}>
        <Fallback width={55} height={22} shape="squircle" />
        <Fallback width={70} height={22} shape="squircle" />
        <Fallback width={80} height={22} shape="squircle" />
      </HStack>
    </VStack>
  );
}

function TokenPairSelector({
  symbol,
  imageUrl,
  currencies,
  tokensMap,
  disabled,
  handleSelect,
  testID,
}: TokenPairSelectorProps) {
  return (
    <Select
      compact
      value={symbol}
      onChange={handleSelect}
      startNode={
        imageUrl && (
          <Box paddingX={1.5}>
            <RemoteImage
              source={imageUrl}
              shape="circle"
              size="m"
              testID={`${testID}-token-image`}
            />
          </Box>
        )
      }
      valueLabel={symbol}
      placeholder="Select a token"
      disabled={disabled}
      testID={`${testID}-token-select`}
    >
      {currencies.map((currency) => (
        <SelectOption
          media={
            <RemoteImage
              source={tokensMap[currency]?.imageUrl ?? ''}
              shape="circle"
              size="l"
              testID={`${testID}-option-image-${tokensMap[currency]?.displaySymbol}`}
            />
          }
          value={currency}
          key={currency}
          title={currency}
          description={tokensMap[currency]?.name ?? ''}
          testID={`${testID}-option-${tokensMap[currency]?.displaySymbol}`}
        />
      ))}
    </Select>
  );
}

function TokenPairAddLiquidityTokenPairSelector() {
  const chainId = useChainId();

  const {
    positionId,
    assetSymbols,
    setAssetSymbols,
    pools,
    feePercent,
    positionTokens,
    currencyFilters,
    poolId,
  } = useAddLiquidityState();

  const disabled = !!positionId;
  const isUSDCTokenPair = positionTokens.some((token) => token.symbol === 'USDC');

  // Fetch pool APR data
  const { data: poolsData } = usePoolsInfo({ chainId, poolId: poolId ?? undefined });
  const apr7dPercentage = useMemo(() => {
    if (!poolId || !poolsData?.poolsInfo) return null;
    const pool = poolsData.poolsInfo.find((p) => p.poolId === poolId);
    return pool?.apr7dPercentage ?? null;
  }, [poolId, poolsData?.poolsInfo]);

  // Filter pools based on currencyFilters if provided
  // This allows limiting the available pools to only those containing specific currencies
  // from URL parameters (e.g., ?currencyA=0x123&currencyB=0x456)
  const filteredPools = useMemo(() => {
    // If no currency filters are provided, return all available pools
    if (!currencyFilters || (!currencyFilters.currencyA && !currencyFilters.currencyB)) {
      return pools;
    }

    // Filter pools to only include those that contain at least one of the specified currencies
    return pools.filter(({ key }) => {
      // Normalize currency addresses to lowercase for case-insensitive comparison
      const currency0 = key.currency0.toLowerCase();
      const currency1 = key.currency1.toLowerCase();
      const filterCurrencyA = currencyFilters.currencyA?.toLowerCase();
      const filterCurrencyB = currencyFilters.currencyB?.toLowerCase();

      // A pool matches if either of its currencies matches either of the filter currencies
      return (
        (filterCurrencyA && (currency0 === filterCurrencyA || currency1 === filterCurrencyA)) ||
        (filterCurrencyB && (currency0 === filterCurrencyB || currency1 === filterCurrencyB))
      );
    });
  }, [pools, currencyFilters]);

  // Build a map of tokens to their asset configs using only the filtered pools
  // This ensures token selectors only show tokens from pools that match the currency filters
  const tokensMap = useMemo(() => {
    return filteredPools.reduce(
      (acc, { key, tokens }) => {
        const poolKey = key;
        const assetConfigs = [
          assetByAddress(chainId, poolKey.currency0),
          assetByAddress(chainId, poolKey.currency1),
        ];
        tokens.forEach((token) => {
          const assetConfig = assetConfigs.find((asset) => asset?.displaySymbol === token);
          acc[token] = assetConfig;
        });
        return acc;
      },
      {} as Record<string, AssetConfig | undefined>,
    );
  }, [filteredPools, chainId]);

  // Build the selectable pool currencies from filtered pools
  // First array: all unique tokens from filtered pools (for first selector)
  // Second array: tokens that can pair with the currently selected first token (for second selector)
  const currencies = useMemo(
    () => [
      filteredPools
        .map(({ tokens }) => tokens)
        .flat()
        .filter((token, i, a) => a.indexOf(token) === i),
      filteredPools
        .filter(({ tokens }) => tokens.includes(assetSymbols[0]))
        .map(({ tokens }) => tokens.filter((token) => token !== assetSymbols[0])[0]),
    ],
    [assetSymbols, filteredPools],
  );

  // Handlers for changing the asset symbols
  // Updated to work with filtered pools instead of all available pools
  const onHandleTokenChange = useMemo(() => {
    return [
      (value: string) => {
        setAssetSymbols((prev) => {
          // If the value is the same as the second asset, swap the assets
          if (value === prev[1]) return [prev[1], prev[0]];

          // Check if the new value is paired with the current second token in filtered pools
          const isPairedWithCurrent = filteredPools.some(
            ({ tokens }) => tokens.includes(value) && tokens.includes(prev[1]),
          );

          // If paired with current second token, just update first token
          if (isPairedWithCurrent) {
            return [value, prev[1]];
          }

          // Otherwise, find first paired asset from filtered pools and update both
          const pairedAsset = filteredPools
            .find(({ tokens }) => tokens.includes(value))
            ?.tokens?.filter((symbol) => symbol !== value)?.[0];

          return pairedAsset ? [value, pairedAsset] : prev;
        });
      },
      (value: string) => {
        setAssetSymbols((prev) => {
          // If the value is the same as the first asset, swap the assets
          if (value === prev[0]) return [prev[1], prev[0]];

          // Check if the new value is paired with the current first token in filtered pools
          const isPairedWithCurrent = filteredPools.some(
            ({ tokens }) => tokens.includes(value) && tokens.includes(prev[0]),
          );

          // If paired with current first token, just update second token
          if (isPairedWithCurrent) {
            return [prev[0], value];
          }

          // Otherwise, find first paired asset from filtered pools and update both
          const pairedAsset = filteredPools
            .find(({ tokens }) => tokens.includes(value))
            ?.tokens?.filter((symbol) => symbol !== value)?.[0];

          return pairedAsset ? [pairedAsset, value] : prev;
        });
      },
    ];
  }, [filteredPools, setAssetSymbols]);

  return (
    <VStack gap={1.5}>
      <HStack justifyContent="space-between" alignItems="center" width="100%">
        <TextHeadline as="p" testID="token-pair-selector-title" noWrap>
          Choose a pair
        </TextHeadline>
        <EditAddLiquiditySettings />
      </HStack>

      <HStack gap={1.5} alignItems="stretch" testID="token-pair-selector-container" width="100%">
        <Box
          flexGrow={1}
          flexShrink={1}
          width="50%"
          bordered
          borderRadius={NESTED_CARD_BORDER_RADIUS}
        >
          <TokenPairSelector
            symbol={assetSymbols[0]}
            imageUrl={tokensMap[assetSymbols[0]]?.imageUrl ?? ''}
            currencies={currencies[0]}
            handleSelect={onHandleTokenChange[0]}
            disabled={disabled}
            tokensMap={tokensMap}
            testID="input"
          />
        </Box>

        <Box
          flexGrow={1}
          flexShrink={1}
          width="50%"
          bordered
          borderRadius={NESTED_CARD_BORDER_RADIUS}
        >
          <TokenPairSelector
            symbol={assetSymbols[1]}
            imageUrl={tokensMap[assetSymbols[1]]?.imageUrl ?? ''}
            currencies={currencies[1]}
            handleSelect={onHandleTokenChange[1]}
            disabled={disabled}
            tokensMap={tokensMap}
            testID="output"
          />
        </Box>
      </HStack>

      <HStack alignItems="center" gap={0.5}>
        {feePercent && (
          <TagLabel testID="token-pair-selector-fee-percent" colorScheme="gray">
            {format({ value: feePercent, type: DisplayType.Percentage })}
          </TagLabel>
        )}
        {apr7dPercentage !== null && (
          <TagLabel testID="token-pair-selector-apr" colorScheme="gray">
            {format({ value: apr7dPercentage, type: DisplayType.Percentage })} APR
          </TagLabel>
        )}
        {isUSDCTokenPair && <USDCRewardsChip />}
      </HStack>
    </VStack>
  );
}

function PositionAddLiquidityTokenPairSelector() {
  const chainId = useChainId();
  const { positionId, positionTokens, tickInRange, feePercent, poolId } = useAddLiquidityState();
  const isUSDCTokenPair = positionTokens.some((token) => token.symbol === 'USDC');

  // Fetch APR data
  const { data: poolsData } = usePoolsInfo({ chainId, poolId: poolId ?? undefined });
  const apr7dPercentage = useMemo(() => {
    if (!poolId || !poolsData?.poolsInfo) return null;
    const pool = poolsData.poolsInfo.find((p) => p.poolId === poolId);
    return pool?.apr7dPercentage ?? null;
  }, [poolId, poolsData?.poolsInfo]);

  // Determine base and quote tokens based on preference
  const token0AsPreferredQuoteToken = isToken0PreferredQuoteToken(
    positionTokens[0].symbol,
    positionTokens[1].symbol,
  );
  const [baseToken, quoteToken] = useMemo(() => {
    return token0AsPreferredQuoteToken
      ? [positionTokens[1], positionTokens[0]]
      : [positionTokens[0], positionTokens[1]];
  }, [positionTokens, token0AsPreferredQuoteToken]);

  // Build token assets for PoolsListTokensMedia using the full asset config
  const tokenAssets = useMemo(
    () => [baseToken.asset, quoteToken.asset],
    [baseToken.asset, quoteToken.asset],
  );

  return (
    <VStack
      padding={NESTED_CARD_PADDING}
      background="bgAlternate"
      borderRadius={NESTED_CARD_BORDER_RADIUS}
    >
      {/* Header content: Left side (icons, name, tags) and right side (In range - vertically centered) */}
      <HStack justifyContent="space-between" alignItems="center" gap={1}>
        {/* Left side: Token icons, name, and tags stacked vertically */}
        <VStack gap={NESTED_CARD_GAP} alignItems="flex-start">
          <HStack gap={1} alignItems="center">
            <PoolsListTokensMedia assets={tokenAssets} size="xl" />
            <TextTitle3 as="p" testID={`position-${positionId}-title`} noWrap>
              {`${baseToken.symbol} / ${quoteToken.symbol}`}
            </TextTitle3>
          </HStack>

          {/* Tags row */}
          <HStack gap={0.5} alignItems="center">
            <TagLabel testID={`position-${positionId}-fee`} colorScheme="gray">
              {numberFormatters.percentFee.format(feePercent)}
            </TagLabel>
            {apr7dPercentage !== null && (
              <TagLabel colorScheme="gray">
                {numberFormatters.apr.format(apr7dPercentage)} APR
              </TagLabel>
            )}
            {isUSDCTokenPair && <USDCRewardsChip />}
          </HStack>
        </VStack>

        {/* Right side: In range indicator (vertically centered) */}
        <HStack gap={0.5} alignItems="center">
          <Icon
            name="dot"
            size="xs"
            active
            color={tickInRange ? 'fgPositive' : 'fgNegative'}
            aria-label={tickInRange ? 'Position in range' : 'Position out of range'}
          />
          <TextBody as="span" color="fgMuted">
            {tickInRange ? 'In range' : 'Out of range'}
          </TextBody>
        </HStack>
      </HStack>
    </VStack>
  );
}

export const AddLiquidityTokenPairSelector = memo(function AddLiquidityTokenPairSelector({
  positionId,
}: {
  positionId: string | null;
}) {
  return positionId ? (
    <PositionAddLiquidityTokenPairSelector />
  ) : (
    <TokenPairAddLiquidityTokenPairSelector />
  );
});
